import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { buildApiUrl, EDGE_FUNCTIONS_URL } from "./api-config";
import { supabase } from "./supabase";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

/**
 * Get authorization headers for Edge Function requests
 * Uses Supabase session token for authentication
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) {
    return { Authorization: `Bearer ${session.access_token}` };
  }
  return {};
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  // Build the full URL (routes to Edge Functions for migrated endpoints)
  const fullUrl = buildApiUrl(url);

  // Prepare headers
  const headers: Record<string, string> = {};
  if (data) {
    headers["Content-Type"] = "application/json";
  }

  // Add auth headers for Edge Function requests
  if (fullUrl.startsWith(EDGE_FUNCTIONS_URL) && EDGE_FUNCTIONS_URL) {
    const authHeaders = await getAuthHeaders();
    Object.assign(headers, authHeaders);
  }

  const res = await fetch(fullUrl, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    // Build the URL from queryKey (e.g., ["/api", "sequences", id])
    const path = queryKey.join("/") as string;
    const fullUrl = buildApiUrl(path);

    // Prepare headers for Edge Function requests
    const headers: Record<string, string> = {};
    if (fullUrl.startsWith(EDGE_FUNCTIONS_URL) && EDGE_FUNCTIONS_URL) {
      const authHeaders = await getAuthHeaders();
      Object.assign(headers, authHeaders);
    }

    const res = await fetch(fullUrl, {
      headers: Object.keys(headers).length > 0 ? headers : undefined,
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      // Data is considered fresh for 5 minutes (prevents unnecessary refetches)
      staleTime: 5 * 60 * 1000,
      // Keep unused data in cache for 10 minutes
      gcTime: 10 * 60 * 1000,
      // Refetch stale data when window regains focus
      refetchOnWindowFocus: true,
      // Refetch stale data on network reconnect
      refetchOnReconnect: true,
      // Disable automatic refetching at intervals
      refetchInterval: false,
      // Retry failed requests once
      retry: 1,
    },
    mutations: {
      retry: false,
    },
  },
});
